import {BookingReferenceType} from "../../../_models/_booking/_enums/booking-reference-type";
import {
  BookingReferenceEntityState
} from "../../../_models/_booking/_enums/booking-reference-entity-state";
import {CompositionDataHelpers, ICompositionData} from "../Articles/composition-data";
import naturalCompare from "string-natural-compare";

export interface IBookingReference {
  id: string;
  entityId: string;
  entityType: BookingReferenceType;
  entityName?: string;
  compositionData: ICompositionData[];
  /**
   * UTC timestamp of the most recent booking on this reference, as an ISO-8601 string — or absent when nothing has
   * been booked on it yet (`CheqMe.Web.Contracts.Booking...BookingReference.LastActivityUtc`, projected from
   * `TableOccupancy.GetLastActivityUtc`).
   *
   * Carried on the reference itself, not looked up per table, so a **live SignalR push alone** contains everything
   * the Offene-Tische timers need — no follow-up fetch, no second source of truth (#205). Optional because the
   * monolith serializes with `NullValueHandling.Ignore`, so a null is simply omitted from the payload.
   *
   * Typed `string | Date` because that is what it is at runtime: a reference fetched over `HttpClient` arrives with
   * the app-wide `DateInterceptor` having already turned the ISO string into a `Date`. Read it only through the
   * open-tables `parseTimestamp`, never `Date.parse`/string handling — re-parsing that `Date` shifted every clock on
   * the screen by the viewer's UTC offset (#677).
   */
  lastActivityUtc?: string | Date;
  /**
   * What kind of entity carries this reference — placed on the floor plan, live but unplaced, or soft-deleted
   * (`CheqMe.Web.Contracts.Booking...BookingReference.EntityState`).
   *
   * Stated by the server rather than inferred from a failed client-side join against the floor-plan read: the
   * carrier's `Temporary`/`Active` flags are not otherwise visible here, and guessing from the join is what made
   * the overview call a booking on a **deleted** table "ad hoc" (#205). Optional so an older payload — or a
   * fixture written before the field existed — reads as {@link BookingReferenceEntityState.Placed}.
   */
  entityState?: BookingReferenceEntityState;

  // ---- lifecycle (#204) ---------------------------------------------------------------------------------
  // These come from CheqMe.Web.Contracts.Booking.BookingReferences.BookingReference, the hand-written positional
  // record behind the v2025.4 List/Get/SetSpendingLimit endpoints this screen calls. It is a SEPARATE type from
  // the register-facing BookingReferenceDto, which inherits BookingReferenceBase wholesale — so a field added to
  // that base does NOT reach this app until the web record is extended too. That gap is exactly how the spending
  // limit was once editable here but not displayable; WebBookingReferenceContractTests now pins the projection.
  // They stay OPTIONAL for the same reason lastActivityUtc/entityState are: NullValueHandling.Ignore is global on
  // the server, so an absent value arrives missing rather than null.

  /**
   * UTC timestamp the reference was claimed open; absent/undefined when it was never opened by hand. A `Date` once
   * the `DateInterceptor` has seen it — same reading rule as {@link lastActivityUtc} (#677).
   */
  openedUtc?: string | Date | null;
  /**
   * The employee who opened the reference (id only — the wire carries no name enrichment), or absent.
   *
   * **Reserved, not available.** No client populates this today, so nothing may render it: doing so would print a
   * raw guid at the guests' table. It rides the contract so the field need not be added later under a migration.
   */
  openedByEmployeeId?: string | null;
  /** The maximum open balance permitted; `null`/`0`/absent all mean "no limit" (see BookingReferenceBase). */
  spendingLimit?: number | null;
  /** UTC ISO timestamp the reference was closed — by the settle policy or by hand; absent while it is open. */
  closedUtc?: string | null;
  /** Whether the reference is currently open. Stated by the server, never re-derived from the two timestamps. */
  isOpen?: boolean;
}

export class BookingReferenceHelpers {
  public static getTotalPriceSingle(reference: IBookingReference): number {
    return reference.compositionData
      .map(composition => CompositionDataHelpers.getTotalPrice(composition))
      .reduce((acc, curr) => acc += curr, 0)
  }

  public static getTotalPriceMany(references: IBookingReference[]): number {
    return references
      .map(reference => this.getTotalPriceSingle(reference))
      .reduce((acc, curr) => acc += curr, 0)
  }

  public static getSortedCompositionDataByName(reference: IBookingReference): ICompositionData[] {
    return reference.compositionData
      .sort((a, b) => naturalCompare(a.composition.name, b.composition.name, {caseInsensitive: true}))
  }

  /** Whether a reference currently carries a spending limit — `null`/`0`/absent all mean "no limit" (#204). */
  public static hasSpendingLimit(reference: IBookingReference): boolean {
    return reference.spendingLimit !== null && reference.spendingLimit !== undefined && reference.spendingLimit !== 0;
  }

  /** Remaining headroom under the spending limit (limit − current total); `null` when there is no limit set. */
  public static getHeadroom(reference: IBookingReference): number | null {
    if (!this.hasSpendingLimit(reference)) return null;
    return reference.spendingLimit! - this.getTotalPriceSingle(reference);
  }
}
